import { ActionFunction, redirect } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { saveData } from './saveData';
import { removeNewSurveillanceRouteStorage } from '../../../../utils/resetData';
import { showSuccessToast, showBackendErrorToast } from '../../../../utils/toasts';
import i18n from '../../../../i18n';
import { isOfflineQueuedError } from '../../../../offline/isOfflineQueuedError';

/**
 * Limpia el borrador de Operarios del wizard de creación tras encolar offline.
 * El éxito online lo hace dentro de `saveData` (mismas 4 keys), pero el throw
 * del encolado ocurre ANTES de llegar a esa limpieza, así que lo replicamos
 * aquí (espejo de `clearEditOperatorsDraft` de EditRoute/action.ts).
 */
function clearNewRouteOperatorsDraft(): void {
  removeNewSurveillanceRouteStorage('new');
}

/**
 * Tour P5 (2026-07-07) — action del wizard "Nuevo recorrido" (patrón
 * pass-data/save-data de la app, mismo esqueleto que
 * `NewSurveillanceRouteOrder/Summary/action.ts`). El componente valida
 * (yup Ubicación + FR-3.4 tripulación, con su UX de frases rojas y salto
 * de tab) y SOLO cuando todo pasa hace submit con intent 'save-data'; la
 * creación (mutation + limpieza del borrador + redirect al detalle) vive
 * aquí, fuera del componente.
 */
export const ActionNewRoute: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const intent = formData.get('intent');

  if (intent === 'save-data') {
    try {
      return await saveData(formData);
    } catch (err) {
      // Offline encolado (espejo EditRoute/NewOrder): OfflineQueueLink encola
      // la SaveSurveillanceRoute (cola simple, clientOpId idempotente) y lanza
      // el centinela `offlineQueued`. Es un ÉXITO diferido, NO un error: al
      // reconectar, syncOfflineQueue la reproduce. Limpiamos el borrador y
      // volvemos al listado (aún no hay id de servidor para ir al detalle).
      // Sin esto, el "error" llegaba a Sentry + toast genérico y el usuario
      // se quedaba atascado en el formulario pese a estar la op ya encolada.
      if (isOfflineQueuedError(err)) {
        clearNewRouteOperatorsDraft();
        showSuccessToast(i18n.t('modals:changeState.queuedOffline'));
        window.dispatchEvent(new CustomEvent('offline-route-queued'));
        return redirect('/surveillance-route');
      }
      Sentry.captureException(err);
      console.error('Error creating surveillance route:', err);
      // Error global de guardado → toast (patrón de la app: los errores
      // inline son por-campo o en-card; el canal global es showErrorToast,
      // el mismo que ya usa useSurveillanceRoutes en el resto del módulo).
      // Escalera del backend (código estable → traducción; negocio 4xx → mensaje
      // crudo; técnico → genérico), como ya hace useSurveillanceRoutes.
      showBackendErrorToast(err, i18n.t('toasts:error.generic'));
      return null;
    }
  }

  return null;
};
